import { useState, useEffect } from "react";
import { FormattedMessage, useIntl } from "react-intl";
import { useFormContext, useWatch } from "react-hook-form";
import moment from "moment";
import momentTz from "moment-timezone";

import Dropdown from "components/atoms/Dropdown/Dropdown";
import Textarea from "components/atoms/Textarea/Textarea";
import Alert from "components/molecules/Alert/Alert";
import TimeInput from "components/atoms/TimeInput/TimeInput";
import useFeatureFlag from "hooks/useFeatureFlag";
import { FEATURE_FLAGS } from "constants/featureFlags";
import testIds from "constants/testIds";
import FormItemInfo from "../Common/FormItemInfo";
import FormHr from "../Common/FormHr";
import useScheduleHourNotesHook from "./hooks/useScheduleHourNotesHook";
import { SERVICE_LEVEL_OPTIONS, cargoReadyDeliveryOption, cargoReadyPickupOption } from "./ScheduleDefaultOptions";
import PickupDeliveryDate from "./PickupDeliveryDate";
import { IDropdown, IPickupDeliveryPlanningProps } from "./Types";

function PickupDeliveryPlanning(props: IPickupDeliveryPlanningProps) {
  const {
    isOffPlatformShipment,
    isPickupOrDelivery,
    scheduleOptions,
    isMiniTender,
    formState,
    isNewShipment,
    countries,
    timezones,
    isConsolidated,
    pickupDateRange,
    stop1DateRange,
    filteredPickupOrders,
    filteredDeliveryOrders,
  } = props || {};

  const [asapPickUpOptions, setAsapPickupOptions] = useState<any>([]);
  const [asapDeliveryOptions, setDeliveryOptions] = useState<any>([]);
  const [holidays, setHolidays] = useState<any>({});
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const [update, setUpdate] = useState(false);

  const intl = useIntl();
  const methods = useFormContext();
  const { setValue, getValues, control } = methods;
  const isOptionalPortAddressFormEnabled = useFeatureFlag(FEATURE_FLAGS.FF_WEB10382_OPTIONAL_PORT_ADDRESS_FORM);
  const { showHour, showNotes, handleShowHour, handleShowNotes } = useScheduleHourNotesHook({ isPickupOrDelivery });

  const {
    allow_asap_delivery,
    off_platform_shipment_optional_details,
    service_levels: isServiceLevelEnabled,
  } = scheduleOptions as any;

  const countryWatch = useWatch({ name: `schedule.${isPickupOrDelivery}_country`, control });
  const scheduleAddressType = useWatch({ name: `schedule.${isPickupOrDelivery}_address_type`, control });
  const isAirportOrSeaportSchedule =
    isOptionalPortAddressFormEnabled && (scheduleAddressType === "airport" || scheduleAddressType === "seaport");
  const stop1AfterDate = useWatch({ name: "schedule.stop1_after_date", control });
  const asapDelivery =
    isPickupOrDelivery === "pickup" ? getValues("schedule.pickup_cargo_ready") : getValues("schedule.delivery_cargo_ready");
  const asapDefaultValue = isPickupOrDelivery === "pickup" ? "asap_pickup" : "asap_delivery";

  useEffect(() => {
    const asapPickupOption = [
      {
        label: intl.formatMessage({
          id: "new-request.schedule.defaultCargoReadyPickupOption.asap",
          defaultMessage: "ASAP Pickup",
        }),
        value: "asap_pickup",
      },
    ];
    const asapDeliveryOption = [
      {
        label: intl.formatMessage({
          id: "new-request.schedule.defaultCargoReadyDeliveryOption.asap",
          defaultMessage: "ASAP Delivery",
        }),
        value: "asap_delivery",
      },
    ];
    if (isNewShipment) {
      setValue(`schedule.pickup_ready_date`, moment().format("YYYY-MM-DD"));
      setValue("schedule.pickup_cargo_ready", "asap_pickup");
      setValue("schedule.delivery_cargo_ready", "asap_delivery");
    }
    setAsapPickupOptions([...cargoReadyPickupOption(intl), ...asapPickupOption]);
    setDeliveryOptions([...cargoReadyDeliveryOption(intl), ...asapDeliveryOption]);
  }, [isNewShipment]);

  const getFormattedDateTime = (key: string, timezoneKey: string) => {
    const dateKey = `${key}_date`;
    const timeKey = `${key}_time`;
    return momentTz.tz(`${getValues(dateKey)} ${getValues(timeKey)}`, getValues(timezoneKey));
  };

  const isValidDeliveryDate = () => {
    const pickupTime = getFormattedDateTime("schedule.pickup_ready", "schedule.pickup_timezone");
    const deliveryTime = getFormattedDateTime("schedule.stop1_after", "schedule.stop1_timezone");
    return deliveryTime.isAfter(pickupTime);
  };

  const isValidPickupBeforeDate = () => {
    const pickupBeforeTime = getFormattedDateTime("schedule.pickup_before", "schedule.pickup_timezone");
    const pickupReadyTime = getFormattedDateTime("schedule.pickup_ready", "schedule.pickup_timezone");
    return pickupBeforeTime.isAfter(pickupReadyTime);
  };

  const isValidDeliveryBeforeDate = () => {
    const deliveryBeforeTime = getFormattedDateTime("schedule.stop1_before", "schedule.stop1_timezone");
    const deliveryAfterTime = getFormattedDateTime("schedule.stop1_after", "schedule.stop1_timezone");
    return deliveryBeforeTime.isAfter(deliveryAfterTime);
  };

  const isValidHours = (value: any): boolean =>
    moment(value, "HH:mm:ss").isAfter(
      moment(getValues(`schedule.${isPickupOrDelivery === "pickup" ? "pickup" : "delivery"}_hours_from`), "HH:mm:ss"),
    );

  const handleCargoReadyDefault = () => {
    if (isPickupOrDelivery === "pickup" && isNewShipment) {
      return asapPickUpOptions?.[2]?.value;
    }
    if (isPickupOrDelivery === "pickup") {
      return cargoReadyPickupOption(intl)?.[0]?.value;
    }
    if (isPickupOrDelivery === "stop1" && isNewShipment) {
      return asapDeliveryOptions?.[2]?.value;
    }
    return cargoReadyDeliveryOption(intl)?.[0]?.value;
  };

  const getCargoOptions = () => {
    if (isPickupOrDelivery === "pickup") {
      return isNewShipment ? asapPickUpOptions : cargoReadyPickupOption(intl);
    }
    return allow_asap_delivery ? asapDeliveryOptions : cargoReadyDeliveryOption(intl);
  };

  function OrdersAlert({ orders, type }: any) {
    if (!orders?.length) return null;

    return (
      <Alert variant="warning" className="text-black w-100">
        <div className="font-bold">
          <FormattedMessage
            id={type === "pickup" ? "new-request.schedule.orderAlert" : "new-request.schedule.orderDeliveryAlert"}
            defaultMessage={
              type === "pickup"
                ? "These orders do not fall within the selected pickup time:"
                : "These orders do not fall within the selected delivery time:"
            }
          />
        </div>
        <ul style={{ listStyleType: "disc" }}>
          {orders.map((order: string[], index: number) => (
            <li key={index}>&#x2022; {order}</li>
          ))}
        </ul>
      </Alert>
    );
  }

  return (
    <>
      <FormHr margin={`${isConsolidated ? "3xl" : "xl"}`} />
      <div className="mt-3xl mb-3xl">
        {(!isOffPlatformShipment || (isOffPlatformShipment && off_platform_shipment_optional_details)) && !isMiniTender ? (
          <div className="d-flex">
            <div className="w-200 mr-xl">
              <FormItemInfo
                labelId={`${
                  isPickupOrDelivery === "pickup"
                    ? "new-request.schedule.pickupPlanning"
                    : "new-request.schedule.deliveryPlanning"
                }`}
                label={`${isPickupOrDelivery === "pickup" ? "Pickup planning" : "Delivery planning"}`}
                descId={`${
                  isPickupOrDelivery === "pickup"
                    ? "new-request.schedule.pickupPlanningInfo"
                    : "new-request.schedule.deliveryPlanningInfo"
                }`}
                description={`${
                  isPickupOrDelivery === "pickup" ? "Provide your pickup schedule" : "Provide your delivery schedule"
                }`}
              />

              <div className="cursor-pointer text-blue-200 font-b3 font-weight-bold mt-xs" onClick={handleShowHour}>
                {showHour
                  ? `${
                      isPickupOrDelivery === "pickup"
                        ? intl.formatMessage({
                            id: "new-request.schedule.removePickupSpecifyHour",
                            defaultMessage: "Remove pickup hours",
                          })
                        : intl.formatMessage({
                            id: "new-request.timing.removeDeliverySpecifyHour",
                            defaultMessage: "Remove delivery hours",
                          })
                    }`
                  : `${
                      isPickupOrDelivery === "pickup"
                        ? intl.formatMessage({
                            id: "new-request.schedule.addPickupSpecifyHour",
                            defaultMessage: "Add pickup hours",
                          })
                        : intl.formatMessage({
                            id: "new-request.schedule.addDeliverySpecifyHour",
                            defaultMessage: "Add delivery hours",
                          })
                    }`}
              </div>

              {!isOffPlatformShipment && !isMiniTender && (
                <div className="cursor-pointer text-blue-200 font-b3 font-weight-bold mt-xs" onClick={handleShowNotes}>
                  {showNotes
                    ? `${
                        isPickupOrDelivery === "pickup"
                          ? intl.formatMessage({
                              id: "new-request.schedule.removePickupInstruction",
                              defaultMessage: "Remove pickup instructions",
                            })
                          : intl.formatMessage({
                              id: "new-request.schedule.removeDeliveryInstruction",
                              defaultMessage: "Remove delivery instructions",
                            })
                      }`
                    : `${
                        isPickupOrDelivery === "pickup"
                          ? intl.formatMessage({
                              id: "new-request.timing.addPickupInstructions",
                              defaultMessage: "Add pickup instructions",
                            })
                          : intl.formatMessage({
                              id: "new-request.timing.addDeliveryInstructions",
                              defaultMessage: "Add delivery instructions",
                            })
                      }`}
                </div>
              )}
            </div>
            <div>
              {isPickupOrDelivery !== "pickup" && isServiceLevelEnabled && (
                <div className={`d-flex flex-column w-192 ${getValues("service.service_level") === "NA" ? "mb-3" : ""}`}>
                  <label className="required">
                    <FormattedMessage id="new-request.service.serviceLevel" defaultMessage="Service level" />
                  </label>
                  <Dropdown
                    options={SERVICE_LEVEL_OPTIONS(intl)}
                    name="service.service_level"
                    defaultValue=""
                    required
                    useHookForm
                    disabled={formState.disabledFields["service.service_level"]}
                  />
                </div>
              )}
              {isConsolidated && (
                <div className="font-b3 mb-xl">
                  <div className="font-bold">
                    {pickupDateRange ? (
                      <FormattedMessage
                        id="new-request.schedule.pickupDateRange"
                        defaultMessage="Pick up time range of selected orders"
                      />
                    ) : (
                      <FormattedMessage
                        id="new-request.schedule.deliveryDateRange"
                        defaultMessage="Delivery time range of selected orders"
                      />
                    )}
                  </div>
                  <div>{pickupDateRange || stop1DateRange}</div>
                </div>
              )}
              {(isPickupOrDelivery === "pickup" ||
                !isServiceLevelEnabled ||
                (isServiceLevelEnabled && getValues("service.service_level") === "NA")) && (
                <div>
                  {!isOffPlatformShipment && (
                    <div className="w-192 dropdown-div mb-3">
                      <Dropdown
                        useHookForm
                        name={
                          isPickupOrDelivery === "pickup" ? `schedule.pickup_cargo_ready` : `schedule.delivery_cargo_ready`
                        }
                        className="schedule-dropdown"
                        defaultValue={handleCargoReadyDefault()}
                        options={getCargoOptions()}
                        disabled={
                          isPickupOrDelivery === "pickup"
                            ? formState.disabledFields[`schedule.pickup_cargo_ready`]
                            : formState.disabledFields[`schedule.delivery_cargo_ready`]
                        }
                        setUpdate={setUpdate}
                        required
                      />
                    </div>
                  )}
                  <div className="flex-row mt-2 main-wrapper">
                    {asapDelivery !== asapDefaultValue && (
                      <>
                        <div className="w-192 align-date">
                          <div
                            data-testid={
                              isPickupOrDelivery === "pickup"
                                ? testIds.REQUEST_FORM_SCHEDULE_PICKUP_READY_DATE
                                : testIds.REQUEST_FORM_SCHEDULE_DELIVERY_AFTER_DATE
                            }
                          >
                            <PickupDeliveryDate
                              datePickerProps={{
                                name:
                                  isPickupOrDelivery === "pickup"
                                    ? `schedule.${isPickupOrDelivery}_ready_date`
                                    : `schedule.${isPickupOrDelivery}_after_date`,
                                validations: {
                                  ...(isPickupOrDelivery !== "pickup" && {
                                    isValidDeliveryDate: () =>
                                      isValidDeliveryDate() ||
                                      intl.formatMessage({
                                        id: "new-request.schedule.deliveryReadyDateErrorMessage",
                                        defaultMessage: "Delivery ready date must be after pickup ready date",
                                      }),
                                  }),
                                },
                                registerOptions: {
                                  deps:
                                    isPickupOrDelivery === "pickup"
                                      ? stop1AfterDate
                                        ? ["schedule.stop1_after_date", "schedule.pickup_before_date"]
                                        : []
                                      : ["schedule.stop1_before_date"],
                                },
                                required: true,
                              }}
                              isPickupOrDelivery={isPickupOrDelivery}
                              countries={countries}
                              holidays={holidays}
                              setHolidays={setHolidays}
                            />
                          </div>
                        </div>
                        <div
                          data-testid={
                            isPickupOrDelivery === "pickup"
                              ? testIds.REQUEST_FORM_SCHEDULE_PICKUP_READY_TIME
                              : testIds.REQUEST_FORM_SCHEDULE_DELIVERY_AFTER_TIME
                          }
                        >
                          <TimeInput
                            name={
                              isPickupOrDelivery === "pickup"
                                ? `schedule.${isPickupOrDelivery}_ready_time`
                                : `schedule.${isPickupOrDelivery}_after_time`
                            }
                            required
                            defaultValue={isPickupOrDelivery === "pickup" ? "09:00:00" : "17:00:00"}
                            registerOptions={{
                              deps: ["schedule.stop1_after_date", `schedule.${isPickupOrDelivery}_before_date`],
                            }}
                          />
                        </div>
                        <div className="w-192 timezone dropdown-div">
                          <Dropdown
                            useHookForm
                            disabled={
                              formState.disabledFields[`schedule.${isPickupOrDelivery}_timezone`] ||
                              (!isAirportOrSeaportSchedule && !countryWatch)
                            }
                            name={`schedule.${isPickupOrDelivery}_timezone`}
                            dataTestid={
                              isPickupOrDelivery === "pickup"
                                ? testIds.REQUEST_FORM_SCHEDULE_PICKUP_TIMEZONE
                                : testIds.REQUEST_FORM_SCHEDULE_DELIVERY_TIMEZONE
                            }
                            className="schedule-dropdown"
                            defaultValue={getValues(`schedule.${isPickupOrDelivery}_timezone`)}
                            placeholder={intl.formatMessage({
                              id: "general.pleaseSelectTimeZone",
                              defaultMessage: "Please select timezone",
                            })}
                            options={timezones}
                            onChange={(item: IDropdown) => {
                              setValue(`schedule.${isPickupOrDelivery}_timezone`, item.value);
                            }}
                            setUpdate={setUpdate}
                            required
                          />
                        </div>
                      </>
                    )}
                  </div>
                  {(isPickupOrDelivery === "pickup" && getValues(`schedule.pickup_cargo_ready`) === "between") ||
                  (isPickupOrDelivery === "stop1" && getValues(`schedule.delivery_cargo_ready`) === "between") ? (
                    <div className="flex-row mt-2 main-wrapper">
                      <div className="w-192 align-date">
                        <div
                          data-testid={
                            isPickupOrDelivery === "pickup"
                              ? testIds.REQUEST_FORM_SCHEDULE_PICKUP_BEFORE_DATE
                              : testIds.REQUEST_FORM_SCHEDULE_DELIVERY_BEFORE_DATE
                          }
                        >
                          <PickupDeliveryDate
                            datePickerProps={{
                              name: `schedule.${isPickupOrDelivery}_before_date`,
                              validations: {
                                ...(isPickupOrDelivery === "pickup"
                                  ? {
                                      isValidPickupBeforeDate: () =>
                                        isValidPickupBeforeDate() ||
                                        intl.formatMessage({
                                          id: "new-request.schedule.pickupBeforeDateErrorMessage",
                                          defaultMessage: "Pickup before date must be after pickup ready date",
                                        }),
                                    }
                                  : {
                                      isValidDeliveryBeforeDate: () =>
                                        isValidDeliveryBeforeDate() ||
                                        intl.formatMessage({
                                          id: "new-request.schedule.deliveryBeforeDateErrorMessage",
                                          defaultMessage: "Delivery before date must be after delivery ready date",
                                        }),
                                    }),
                              },
                              required: true,
                            }}
                            isPickupOrDelivery={isPickupOrDelivery}
                            countries={countries}
                            holidays={holidays}
                            setHolidays={setHolidays}
                          />
                        </div>
                      </div>
                      <div
                        data-testid={
                          isPickupOrDelivery === "pickup"
                            ? testIds.REQUEST_FORM_SCHEDULE_PICKUP_BEFORE_TIME
                            : testIds.REQUEST_FORM_SCHEDULE_DELIVERY_BEFORE_TIME
                        }
                      >
                        <TimeInput
                          name={`schedule.${isPickupOrDelivery}_before_time`}
                          required
                          registerOptions={{
                            deps: `schedule.${isPickupOrDelivery}_before_date`,
                          }}
                        />
                      </div>
                    </div>
                  ) : null}
                </div>
              )}
              <div>
                {!isOffPlatformShipment &&
                  !isMiniTender &&
                  (!isServiceLevelEnabled ||
                    (isServiceLevelEnabled && isPickupOrDelivery === "pickup") ||
                    (isServiceLevelEnabled && getValues("service.service_level") === "NA")) && (
                    <>
                      {showHour ||
                      (isPickupOrDelivery === "pickup"
                        ? getValues("schedule.pickup_hours_from") && getValues("schedule.pickup_hours_to")
                        : getValues("schedule.delivery_hours_from") && getValues("schedule.delivery_hours_to")) ? (
                        <div className="flex-row">
                          <div className="w-87 mt-2">
                            <label>
                              <FormattedMessage id="new-request.schedule.specifyHoursFrom" defaultMessage="From" />
                            </label>
                            <TimeInput
                              name={
                                isPickupOrDelivery === "pickup"
                                  ? "schedule.pickup_hours_from"
                                  : "schedule.delivery_hours_from"
                              }
                              defaultValue="09:00:00"
                              required
                              registerOptions={{
                                deps: `schedule.${isPickupOrDelivery === "pickup" ? "pickup" : "delivery"}_hours_to`,
                              }}
                            />
                          </div>
                          <div className="w-87 mt-2">
                            <label>
                              <FormattedMessage id="new-request.schedule.specifyHoursTo" defaultMessage="To" />
                            </label>
                            <TimeInput
                              name={
                                isPickupOrDelivery === "pickup" ? "schedule.pickup_hours_to" : "schedule.delivery_hours_to"
                              }
                              defaultValue="18:00:00"
                              required
                              validations={{
                                isValidPickupHours: (v: any) =>
                                  isValidHours(v) ||
                                  intl.formatMessage({
                                    id: "new-request.schedule.specifyPickupHourErrorMessage",
                                    defaultMessage: "End time should be greater than start time",
                                  }),
                              }}
                            />
                          </div>
                        </div>
                      ) : null}
                    </>
                  )}
              </div>
              <div>
                <div className="mb-xl">
                  {showNotes || getValues(`schedule.${isPickupOrDelivery}_notes`) ? (
                    <Textarea
                      name={`schedule.${isPickupOrDelivery}_notes`}
                      testId={
                        isPickupOrDelivery === "pickup"
                          ? testIds.REQUEST_FORM_SCHEDULE_PICKUP_NOTES
                          : testIds.REQUEST_FORM_SCHEDULE_DELIVERY_NOTES
                      }
                      placeholder={intl.formatMessage({
                        id: `${
                          isPickupOrDelivery === "pickup"
                            ? "new-request.schedule.pickupInstructions"
                            : "new-request.schedule.deliveryInstructions"
                        }`,
                        defaultMessage: `${
                          isPickupOrDelivery === "pickup" ? "Add pickup instructions" : "Add delivery instructions"
                        }`,
                      })}
                      rows={2}
                      className="text-body bg-white w-392 mt-3"
                    />
                  ) : (
                    <p className="w-392 mt-3" />
                  )}
                </div>
              </div>
            </div>
            {isPickupOrDelivery !== "pickup" && isServiceLevelEnabled && getValues("service.service_level") !== "NA" && (
              <>
                {getValues("service.service_level") && (
                  <div className="d-flex flex-column i ml-8">
                    <label>
                      <FormattedMessage id="report.DeliveryDate" defaultMessage="Delivery date" />
                    </label>
                    {getValues("schedule.pickup_ready_date") ? (
                      <div className="d-flex align-items-center" style={{ height: 26 }}>
                        <p className="font-b4">
                          {moment(getValues("schedule.stop1_after_date")).format("ddd, DD-MM-YYYY")}{" "}
                          <span className="text-secondary font-b3">(based on selected service level)</span>
                        </p>
                      </div>
                    ) : (
                      <div>
                        <Alert variant="warning">Please select a pickup date</Alert>
                      </div>
                    )}
                  </div>
                )}
              </>
            )}
          </div>
        ) : null}
      </div>

      <div className="flex-row">
        <div className="w-200" style={{ flexShrink: 0 }} />
        {isConsolidated && (
          <div className="mb-lg w-100">
            {filteredPickupOrders && <OrdersAlert orders={filteredPickupOrders} type="pickup" />}
            {filteredDeliveryOrders && <OrdersAlert orders={filteredDeliveryOrders} type="delivery" />}
          </div>
        )}
      </div>
    </>
  );
}

export default PickupDeliveryPlanning;
